import { DrugBadges } from '@/entities/drug/components/DrugBadges';
import { DrugMeta } from '@/entities/drug/components/DrugMeta';
import {
  isDrugNotFoundError,
  useDrugDetail,
} from '@/entities/drug/hooks/useDrugQueries';
import {
  drugKindLabels,
  displayDrugCode,
  formatFeeRate,
} from '@/entities/drug/utils/drug.utils';
import { RelatedDrugsTab } from '@/shared/components/drug-drawer/RelatedDrugsTab';
import { EmptyState, ErrorState } from '@/shared/components/StateViews';
import { Badge } from '@/shared/components/ui/badge';
import { SheetTitle } from '@/shared/components/ui/sheet';
import { Skeleton } from '@/shared/components/ui/skeleton';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/shared/components/ui/tabs';
import { cn } from '@/shared/lib/utils';
import { formatWon } from '@/shared/utils/format';
import DOMPurify from 'dompurify';
import { Fragment, useMemo, type ReactNode } from 'react';

const LABEL_TABS = [
  { value: 'direction', label: '용법용량' },
  { value: 'effect', label: '효능효과' },
  { value: 'precaution', label: '사용상 주의사항' },
] as const;

/**
 * M-12 드로어 본문 (Figma 833:6613)
 * - 흰 영역(833:6932): 약명 20 Bold + ✕ / 성분 / 대조약 · 보험코드 · 제약사 · 급여 · 전문 · 단위 / 최소 처방 금액
 *   → 약가 · 수수료율 · 100회 처방 예상 금액(회색 r20) → 처방 중 병의원 칩 → Tab Square 4종
 * - 회색 영역: 동일성분 의약품(기준 토글 · 동일제형 군 · 정렬 · 검색_결과_상세 카드) / 허가사항 원문
 */
export const DrugDetailView = ({
  drugCode,
  closeButton,
  compactTop = false,
  onSelectRelated,
}: {
  drugCode: string;
  /** 약명 줄 오른쪽 ✕ (드로어 호스트가 넘긴다) */
  closeButton: ReactNode;
  /** 위에 브레드크럼 · 처방량 안내가 있으면 위 여백을 줄인다 */
  compactTop?: boolean;
  /** 동일성분 카드 클릭 — 같은 드로어에서 내용 교체 (현재 약을 브레드크럼에 남긴다) */
  onSelectRelated: (
    current: { drugCode: string; drugName: string },
    nextCode: string,
  ) => void;
}) => {
  const { data, isPending, isError, error, refetch } = useDrugDetail(drugCode);
  const topPadding = compactTop ? 'pt-6' : 'pt-10';

  if (isPending || isError) {
    return (
      <div className={cn('bg-surface-white px-6 pb-10', topPadding)}>
        <div className="flex items-center justify-between gap-6">
          <SheetTitle className="sr-only">의약품 상세</SheetTitle>
          {isPending ? <Skeleton className="h-7 w-3/4" /> : <span />}
          {closeButton}
        </div>
        {isPending ? (
          <DetailSkeleton />
        ) : isDrugNotFoundError(error) ? (
          <EmptyState
            className="py-20"
            message="의약품 정보를 찾을 수 없어요."
          />
        ) : (
          <ErrorState className="py-20" onRetry={() => void refetch()} />
        )}
      </div>
    );
  }

  const { drug } = data;
  // 100회 처방 예상액은 서버 값(제약사 기준)을 그대로 쓴다
  const expected = data.pharmaEstimatedAmount;
  const unit =
    data.unitQuantity != null && data.baseUnit
      ? `${data.unitQuantity}${data.baseUnit}`
      : null;
  const stats = [
    { label: '약가', value: formatWon(drug.price), tone: 'text-fg-primary' },
    {
      label: '수수료율',
      value: formatFeeRate(drug.pharmaFeeRate),
      tone: drug.pharmaFeeRate == null ? 'text-fg-light' : 'text-fg-info',
    },
    {
      label: '100회 처방 예상 금액',
      value: formatWon(expected),
      tone: expected == null ? 'text-fg-light' : 'text-fg-primary',
    },
  ];
  const clinics = data.prescribingClinics;

  return (
    <Tabs defaultValue="related" className="flex flex-col">
      <div className={cn('flex flex-col gap-6 bg-surface-white', topPadding)}>
        <div className="flex flex-col gap-6 px-6">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-[5px]">
              <div className="flex items-center gap-6">
                <SheetTitle className="min-w-0 flex-1 text-heading-20-b text-fg-primary">
                  {drug.drugName}
                </SheetTitle>
                {closeButton}
              </div>
              {drug.mainIngredientName && (
                <p className="text-body-15-r text-fg-tertiary">
                  {[
                    drug.mainIngredientName,
                    drug.mainIngredientNameEnglish &&
                      `(${drug.mainIngredientNameEnglish})`,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-1 text-body-15-r text-fg-tertiary">
              <DrugBadges
                outOfStock={drug.outOfStock}
                newDrugBanned={drug.newDrugBanned}
                className="mb-1"
              />
              <DrugMeta
                items={[
                  ...drugKindLabels(drug),
                  displayDrugCode(drug.drugCode),
                  drug.pharmaCompanyName,
                  drug.reimbursementType,
                  data.etcOtcType,
                  unit,
                ]}
              />
              {drug.minPrescriptionAmount != null && (
                <p>최소 처방 금액 {formatWon(drug.minPrescriptionAmount)}</p>
              )}
            </div>
          </div>

          <dl className="flex items-stretch rounded-20 bg-surface-grey py-4">
            {stats.map((s, i) => (
              <Fragment key={s.label}>
                {i > 0 && <span aria-hidden className="w-px bg-line-grey" />}
                <div className="flex min-w-0 flex-1 flex-col items-center gap-1 text-center">
                  <dt className="text-body-15-r text-fg-tertiary">{s.label}</dt>
                  <dd className={cn('text-subtitle-18-b', s.tone)}>
                    {s.value}
                  </dd>
                </div>
              </Fragment>
            ))}
          </dl>

          {clinics.length > 0 && (
            <section className="flex flex-col gap-3">
              <h3 className="text-subtitle-18-b text-fg-primary">처방 중</h3>
              <ul className="flex flex-wrap gap-x-1 gap-y-3">
                {clinics.map((c) => (
                  <li key={c.clinicId}>
                    <Badge
                      state="clear"
                      emphasis="strong"
                      size="medium"
                      className="text-fg-secondary"
                    >
                      {c.clinicName}
                    </Badge>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>

        <TabsList className="w-full pt-2">
          {[{ value: 'related', label: '동일성분 의약품' }, ...LABEL_TABS].map(
            (t) => (
              <TabsTrigger key={t.value} value={t.value} className="flex-1">
                {t.label}
              </TabsTrigger>
            ),
          )}
        </TabsList>
      </div>

      <TabsContent value="related" className="px-6 pt-6 pb-10 outline-none">
        <RelatedDrugsTab
          drugCode={drugCode}
          onSelect={(next) =>
            onSelectRelated({ drugCode, drugName: drug.drugName }, next)
          }
        />
      </TabsContent>
      {LABEL_TABS.map((t) => (
        <TabsContent
          key={t.value}
          value={t.value}
          className="px-6 pt-6 pb-10 outline-none"
        >
          <LabelHtml html={data[t.value]} />
        </TabsContent>
      ))}
    </Tabs>
  );
};

/**
 * 허가사항 원문 렌더링에 허용하는 태그 — 나머지(스타일 · 링크 등)는 버린다
 * 이미지는 원문에 박힌 base64(data:image) 만 허용한다 — 외부 요청이 생기지 않게 (RDL-160)
 */
const LABEL_ALLOWED_TAGS = [
  'img',
  'p',
  'br',
  'b',
  'strong',
  'i',
  'em',
  'u',
  'sub',
  'sup',
  'span',
  'div',
  'ul',
  'ol',
  'li',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
];

/** 허가사항 이미지로 허용하는 src — svg 는 제외한다 */
const LABEL_IMAGE_SRC = /^data:image\/(png|jpe?g|gif|webp);base64,/i;

/** 허가사항 전용 인스턴스 — 훅이 다른 DOMPurify 사용처에 번지지 않게 분리한다 */
const labelPurify = DOMPurify(window);
labelPurify.addHook('uponSanitizeAttribute', (_node, data) => {
  if (data.attrName === 'src' && !LABEL_IMAGE_SRC.test(data.attrValue)) {
    data.keepAttr = false;
  }
});

/**
 * 허가사항 원문 — 서버가 줄마다 `<p>` 로 감싸고 허용 태그만 남긴 HTML 을 내려준다 (docs/api/drugs.md)
 * 서버 정리와 별개로 프론트에서도 허용 목록으로 한 번 더 정제한 뒤 렌더링한다
 */
const LabelHtml = ({ html }: { html: string | null }) => {
  const clean = useMemo(
    () =>
      html?.trim()
        ? labelPurify
            .sanitize(html, {
              ALLOWED_TAGS: LABEL_ALLOWED_TAGS,
              ALLOWED_ATTR: ['colspan', 'rowspan', 'src', 'alt'],
            })
            .trim()
        : '',
    [html],
  );
  if (!clean)
    return (
      <p className="rounded-20 bg-surface-white py-12 text-center text-body-15-r text-fg-light">
        등록된 정보가 없어요.
      </p>
    );
  return (
    <div
      className="rounded-20 bg-surface-white p-4 text-body-15-r break-keep text-fg-secondary [&_b]:font-bold [&_img]:h-auto [&_img]:max-w-full [&_li]:mb-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-2 [&_p:last-child]:mb-0 [&_strong]:font-bold [&_table]:my-2 [&_table]:w-full [&_td]:border [&_td]:border-line-grey [&_td]:px-2 [&_td]:py-1 [&_th]:border [&_th]:border-line-grey [&_th]:bg-surface-grey [&_th]:px-2 [&_th]:py-1 [&_ul]:list-disc [&_ul]:pl-5"
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
};

const DetailSkeleton = () => (
  <div className="mt-2 flex flex-col gap-3" aria-busy="true">
    <Skeleton className="h-6 w-1/3" />
    <Skeleton className="h-6 w-2/3" />
    <Skeleton className="mt-3 h-[88px] w-full rounded-20" />
    <Skeleton className="mt-3 h-7 w-20" />
    <Skeleton className="h-8 w-3/4" />
    <Skeleton className="mt-4 h-12 w-full" />
  </div>
);
